import React, { Component } from 'react';
import api from '../../services/api';
import { Redirect, Link } from 'react-router-dom';
import { isAuthenticated } from "../../services/auth";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "../../services/recaptcha";

// Debe coincidir con MIN_PASSWORD_LENGTH del modelo app_user en la API, que es
// quien de verdad aplica la regla. Aqui solo evita un viaje de ida y vuelta.
const MIN_PASSWORD_LENGTH = 10;

// bcrypt corta a los 72 BYTES, no a los 72 caracteres. El maxLength del input
// cuenta unidades UTF-16, asi que una contrasena con acentos o emoji puede pasar
// el filtro del navegador y pasarse igualmente del limite del servidor.
const MAX_PASSWORD_BYTES = 72;

// TextEncoder no existe en navegadores muy viejos; ahi se mide por caracteres,
// que es lo mismo mientras la contrasena sea ASCII y siempre menos que el real.
const passwordBytes = (value) => {
  try {
    return new TextEncoder().encode(value).length;
  } catch (error) {
    return value.length;
  }
};

// La API devuelve 422 para cualquier fallo de validacion, asi que el status por
// si solo no distingue un correo repetido de una contrasena corta. Cada caso
// trae un code estable y es eso lo que se traduce.
const ERROR_MESSAGES = {
  MISSING_FIELDS: "Fill in all the fields",
  INVALID_EMAIL: "Enter a valid email address",
  EMAIL_TAKEN: "There is already an account created with this email address",
  PASSWORD_TOO_SHORT: `The password must be at least ${MIN_PASSWORD_LENGTH} characters long`,
  PASSWORD_TOO_COMMON: "That password is too common, please choose another one",
  PASSWORD_TOO_SIMPLE: "The password cannot be a single character repeated",
  PASSWORD_TOO_LONG: "The password is too long (72 characters maximum)",
  INVALID_PASSWORD: "That password is not valid, please choose another one",
  CAPTCHA_MISSING: "Complete the captcha",
  CAPTCHA_INVALID: "The captcha could not be verified, please try again",
};

export default class SignUp extends Component {
  state = {
    register_complete: false,
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company_name: "",
    error: "",
    captcha: null,
    loading: false
  }

  componentDidMount() {
    document.querySelector('body').classList.remove("no-scroll");
  }

  hendleSignUp = async (e) => {
    e.preventDefault();

    const { password, confirmPassword, captcha } = this.state;
    const name = this.state.name.trim();
    const company_name = this.state.company_name.trim();
    const email = this.state.email.trim().toLowerCase();

    if (!name || !email || !company_name || !password || !confirmPassword || !captcha) {
      this.setState({ error: "Fill in all fields and complete the captcha" });
      return;
    }

    // Espejo de la regla del servidor, que es quien de verdad la aplica.
    if (password.length < MIN_PASSWORD_LENGTH) {
      this.setState({ error: ERROR_MESSAGES.PASSWORD_TOO_SHORT });
      return;
    }

    if (passwordBytes(password) > MAX_PASSWORD_BYTES) {
      this.setState({ error: ERROR_MESSAGES.PASSWORD_TOO_LONG });
      return;
    }

    if (password !== confirmPassword) {
      this.setState({ error: "Password confirmation does not match password" });
      return;
    }

    try {
      this.setState({ loading: true, error: "" });

      await api.post("/app_users/user_company", { name, email, password, company_name, captcha });

      // Éxito
      this.setState({ register_complete: true, loading: false });
    } catch (err) {
      this.setState({ loading: false, error: this.describeError(err) });

      // El token es de un solo uso: tras un fallo hay que marcar la casilla otra
      // vez o el servidor rechazara el siguiente intento por token ya gastado.
      if (this.recaptchaRef) {
        try { this.recaptchaRef.reset(); } catch(e) { /* ignore */ }
        this.setState({ captcha: null });
      }
    }
  }

  onCaptchaChange = (token) => {
    // token llega null si caduca o si se resetea el widget.
    this.setState({ captcha: token });
  }

  describeError = (err) => {
    // Un fallo antes de llegar a la API no trae response.
    if (err && !err.response) {
      return "Could not reach the server. Check your connection and try again";
    }

    const code = err?.response?.data?.error?.code;

    if (code && ERROR_MESSAGES[code]) {
      return ERROR_MESSAGES[code];
    }

    // Los 503 (captcha sin configurar, o Google inalcanzable) llegan sin code:
    // strong-error-handler oculta el detalle de los 5xx.
    if (err?.response?.status === 503) {
      return "Registration is temporarily unavailable. Please try again in a few minutes";
    }

    return "A server error occurred while registering your account. Please try again later";
  }

  onChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
      // El error se refiere a lo que se envio, no a lo que se esta escribiendo:
      // dejarlo en pantalla mientras se corrige el campo hace dudar de si la
      // correccion sirvio de algo.
      error: ""
    });
  }

  render() {
    if (isAuthenticated()) {
      return <Redirect to='/projects' />
    }

    const { register_complete, error, loading, captcha } = this.state;

    return (
      <div data-page="sign-up" className="with-text">
        <svg className="bg-logo" aria-hidden="true" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 228.4 224.6">
					<path fill="#2f55a4" d="M218 162.1l-16.4 16.4c-7.8 5.6-18.7 4.9-25.7-2.1l-56.7-56.7-.3-.3-32.7-32.7c-1-1.1-2.1-2.2-3.2-3.2l-13-13-28.3-28.3h28.9c0 .2 4.7 0 7.1 0 5.9 0 11.6 1 16.8 2.9 5.6 2 10.7 5 15.2 8.8L140.8 85l6.3 6.3 10.8 10.8 28.2 28.2 18 18 13.9 13.8zm-42.3 42.8l-16.4 16.4c-7 5-16.4 4.9-23.3-.1-.8-.6-1.6-1.3-2.3-2L87.5 173l-.6-.6-20-20-8.2-8.2h38.6c5.5.3 10.8 1.5 15.8 3.5 5.3 2.1 10.2 5.2 14.5 8.9l4.3 4.3c3 3.4 12.2 12.2 12.2 12.2l18 18 13.6 13.8z"/>
					<path fill="#2f55a4" d="M213.3 73.5l-14-14L157.8 18l-6.5-6.5c-4.3-3.6-9.2-6.3-14.4-8.3s-11-3-16.9-3H1.2c0 .2 27.1 28 27.1 28h64.6c5.6.1 11 1.2 16 3 5.8 2.2 11.2 5.3 15.7 9.5 1.4 1.3 2.8 2.6 4.1 4.1l56.9 56.9c7.4 7.4 19.2 7.7 26.9.9l14.9-14.9c.1-.1-14.1-14.2-14.1-14.2z"/>
				</svg>
		<div className="text-holder">
					<p className="text"><strong>Seat 7 Entertainment</strong><br/>is the fastest growing business development and executive production company specializing in the video game, interactive, and digital space.</p>
				</div>
        <div className="container-sign-up">
          <svg className="bg-logo-right" aria-hidden="true" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 228.4 224.6">
						<path fill="#2f55a4" d="M218 162.1l-16.4 16.4c-7.8 5.6-18.7 4.9-25.7-2.1l-56.7-56.7-.3-.3-32.7-32.7c-1-1.1-2.1-2.2-3.2-3.2l-13-13-28.3-28.3h28.9c0 .2 4.7 0 7.1 0 5.9 0 11.6 1 16.8 2.9 5.6 2 10.7 5 15.2 8.8L140.8 85l6.3 6.3 10.8 10.8 28.2 28.2 18 18 13.9 13.8zm-42.3 42.8l-16.4 16.4c-7 5-16.4 4.9-23.3-.1-.8-.6-1.6-1.3-2.3-2L87.5 173l-.6-.6-20-20-8.2-8.2h38.6c5.5.3 10.8 1.5 15.8 3.5 5.3 2.1 10.2 5.2 14.5 8.9l4.3 4.3c3 3.4 12.2 12.2 12.2 12.2l18 18 13.6 13.8z"/>
						<path fill="#2f55a4" d="M213.3 73.5l-14-14L157.8 18l-6.5-6.5c-4.3-3.6-9.2-6.3-14.4-8.3s-11-3-16.9-3H1.2c0 .2 27.1 28 27.1 28h64.6c5.6.1 11 1.2 16 3 5.8 2.2 11.2 5.3 15.7 9.5 1.4 1.3 2.8 2.6 4.1 4.1l56.9 56.9c7.4 7.4 19.2 7.7 26.9.9l14.9-14.9c.1-.1-14.1-14.2-14.1-14.2z"/>
					</svg>
					<svg className="logo" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 261.7 368.3">
						<path fill="#2f55a4" d="M235.3 164l-16.4 16.4c-7.8 5.6-18.7 4.9-25.7-2.1l-56.7-56.7-.3-.3-32.7-32.7c-1-1.1-2.1-2.2-3.2-3.2l-13-13L59 44.1h28.9c0 .2 4.7 0 7.1 0 5.9 0 11.6 1 16.8 2.9 5.6 2 10.7 5 15.2 8.8l31.1 31.1 6.3 6.3 10.8 10.8 28.2 28.2 18 18 13.9 13.8zM193 206.3l-16.4 16.4c-7 5-16.4 4.9-23.3-.1-.8-.6-1.6-1.3-2.3-2l-46.2-46.2-.6-.6-20-20-8.2-8.2h38.6c5.5.3 10.8 1.5 15.8 3.5 5.3 2.1 10.2 5.2 14.5 8.9l4.3 4.3c3 3.4 12.2 12.2 12.2 12.2l18 18 13.6 13.8z"/>
						<path fill="#2f55a4" d="M231.7 75.7l-14.2-14.2-41.9-41.9L169 13c-4.3-3.6-9.3-6.4-14.6-8.4-5.3-2-11.1-3-17.1-3h-120c0 .2 27.4 28.3 27.4 28.3H110c5.7.1 11.1 1.2 16.2 3 5.9 2.2 11.3 5.4 15.9 9.6 1.4 1.3 2.8 2.6 4.1 4.1l57.5 57.5c7.5 7.5 19.4 7.8 27.2.9L246 89.9l-14.3-14.2z"/>
						<path fill="#2f55a4" d="M30.8 247c10.2 0 15.6 5.4 15.6 15.6V274c0 1.1-.5 1.7-1.7 1.7H33.3c-1.1 0-1.7-.6-1.7-1.7v-8.4c0-2.8-1.4-4.3-4.3-4.3h-5.2c-2.7 0-4.3 1.5-4.3 4.3v14.9l25.7 8.4c1.8.8 2.8 1.9 2.8 4v24.9c0 10.2-5.4 15.6-15.6 15.6h-12c-10 0-15.6-5.4-15.6-15.6v-11.3c0-1.2.6-1.7 1.7-1.7H16c1.2 0 1.7.5 1.7 1.7v8.3c0 2.9 1.6 4.3 4.3 4.3h5.2c2.8 0 4.3-1.4 4.3-4.3v-14.4L6 292c-1.9-.6-2.9-1.9-2.9-4v-25.5c0-10.2 5.5-15.6 15.6-15.6h12.1zM100.2 259.5c0 1.1-.4 1.8-1.7 1.8h-27V283h20.6c1.1 0 1.7.6 1.7 1.8v10.7c0 1.2-.6 1.8-1.7 1.8H71.5v21.8h27c1.3 0 1.7.5 1.7 1.7v10.8c0 1.1-.4 1.7-1.7 1.7H58.7c-1 0-1.6-.6-1.6-1.7v-82.9c0-1.1.6-1.7 1.6-1.7h39.7c1.3 0 1.7.6 1.7 1.7v10.8zM148.4 333.4c-1 0-1.5-.5-1.7-1.5l-3-15.8h-19.2l-2.9 15.8c-.2 1-.8 1.5-1.7 1.5h-11.6c-1.2 0-1.7-.6-1.4-1.8l17.9-83.1c.2-1.1.9-1.5 1.8-1.5h15c1 0 1.6.4 1.8 1.5l17.9 83.1c.2 1.2-.2 1.8-1.5 1.8h-11.4zM134 265.9l-6.9 37.7h13.8l-6.9-37.7zM201.7 247c1.2 0 1.7.6 1.7 1.7v10.8c0 1.1-.5 1.7-1.7 1.7h-12.5v70.4c0 1.2-.5 1.7-1.7 1.7h-11.2c-1.1 0-1.7-.5-1.7-1.7v-70.4H162c-1.1 0-1.7-.6-1.7-1.7v-10.8c0-1.1.6-1.7 1.7-1.7h39.7zM258.1 247c1.7 0 2.2 1.1 1.6 2.6l-31.5 82c-.4 1.2-1.2 1.7-2.5 1.7l-11.2.1c-1.6 0-2.3-1-1.7-2.5l26.7-69.6h-14.8c-6.9 0-12.5-5.6-12.5-12.5 0-1.2.6-1.7 1.7-1.8h44.2z"/>
					</svg>

          {!register_complete &&
            <form className="form" onSubmit={this.hendleSignUp}>
              {/* role="alert" para que el lector de pantalla lo anuncie al
                  aparecer: sin el, el mensaje sale en pantalla y nadie que no
                  este mirando se entera de por que no avanzo el registro. */}
              {error &&
                <div className="label label-error" role="alert">
                  <p className="text">{error}</p>
                </div>
              }

              {/* Cada campo lleva su <label> de verdad, oculto a la vista. El
                  placeholder hacia de nombre accesible, pero desaparece en cuanto
                  se escribe y deja el campo sin identificar al repasar el
                  formulario. El diseno no cambia. */}
              <div className="label">
                <label className="visually-hidden" htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Name"
                  autoComplete="name"
                  maxLength={100}
                  required
                  value={this.state.name}
                  onChange={this.onChange}
                />
              </div>

              <div className="label">
                <label className="visually-hidden" htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  autoComplete="email"
                  maxLength={254}
                  required
                  value={this.state.email}
                  onChange={this.onChange}
                />
              </div>

              <div className="label">
                <label className="visually-hidden" htmlFor="company_name">Company</label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder="Company"
                  autoComplete="organization"
                  maxLength={150}
                  required
                  value={this.state.company_name}
                  onChange={this.onChange}
                />
              </div>

              <div className="label">
                <label className="visually-hidden" htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  maxLength={MAX_PASSWORD_BYTES}
                  aria-describedby="password-hint"
                  required
                  value={this.state.password}
                  onChange={this.onChange}
                />
                <span className="icon"></span>
              </div>

              <div className="label">
                <label className="visually-hidden" htmlFor="confirmPassword">Confirm your password</label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  maxLength={MAX_PASSWORD_BYTES}
                  required
                  value={this.state.confirmPassword}
                  onChange={this.onChange}
                />
              </div>

              <div className="label-hint">
                <p className="text" id="password-hint">
                  {`At least ${MIN_PASSWORD_LENGTH} characters. A long phrase is safer than a short word with symbols.`}
                </p>
              </div>

              <div className="label">
                <ReCAPTCHA
                  ref={el => (this.recaptchaRef = el)}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={this.onCaptchaChange}
                  onExpired={() => this.onCaptchaChange(null)}
                />
              </div>

              <button
                type="submit"
                className={`btn btn-register ${loading ? 'load' : ''}`}
                disabled={!captcha || loading}
                aria-disabled={!captcha || loading}
                aria-describedby={!captcha && !loading ? 'captcha-hint' : undefined}
              >
                {loading ? 'Signing up...' : 'Sign Up'}
              </button>

              {/* El boton nace deshabilitado hasta que se resuelve el captcha.
                  Sin decirlo, quien rellena todo y no se fija en la casilla se
                  encuentra un boton que no responde y nada que lo explique. */}
              {!captcha && !loading &&
                <div className="label-hint">
                  <p className="text" id="captcha-hint">
                    {ERROR_MESSAGES.CAPTCHA_MISSING} to enable Sign Up.
                  </p>
                </div>
              }
            </form>
          }

          {register_complete &&
            <div className="register-complete">
              <p className="title">Welcome!</p>
              <p className="text">
                You have successfully created your account.
                <br/><br/>
                <strong>Keep an eye on your email.</strong><br/>
                You will receive a notice when your account is verified granting you access to the platform.
              </p>
            </div>
          }
        </div>

        <Link to="/login" className="btn button-signin">Sign in</Link>
      </div>
    )
  };
}
